import type { ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils.js';

/*
 * shadcn/ui's button, re-authored against this project's tokens.
 *
 * Two deliberate departures from the stock component:
 *   - Sizes and type scale come from --space-* and --text-*, never from
 *     Tailwind's numeric scale, so a button lines up with everything else.
 *   - No `outline-none` and no `focus-visible:ring-*`. global.css owns the
 *     focus ring for the whole application; a per-component ring would either
 *     double it or, with `outline-none`, silently remove it.
 */
export const buttonVariants = cva(
  cn(
    'inline-flex shrink-0 items-center justify-center gap-(--space-2) whitespace-nowrap',
    'rounded-(--radius-md) font-medium',
    'transition-[background-color,color,border-color,transform] duration-(--duration-fast) ease-(--ease-out-expo)',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-70',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
  ),
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-(--color-accent-hover) active:translate-y-[0.5px] disabled:bg-rule-strong disabled:text-ink-subtle',
        outline:
          'border border-rule-strong bg-card text-foreground hover:bg-surface-hover active:translate-y-[0.5px]',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-surface-hover',
        ghost: 'text-ink-muted hover:bg-surface-hover hover:text-ink',
        destructive:
          'bg-destructive text-destructive-foreground hover:brightness-95 active:translate-y-[0.5px]',
        /* The console's delete affordance: red as an outline, so a destructive
           action is legible without shouting from every row. */
        destructiveOutline: 'border border-danger text-danger hover:bg-(--color-danger-quiet)',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      /* Heights come from the shared control tokens, not from padding: a
         button has to end up exactly as tall as the select and the segmented
         control it sits beside, and padding plus a type scale never quite
         agree. */
      size: {
        default: 'h-(--control-height) px-(--space-4) text-[length:var(--text-body)]',
        sm: 'h-(--control-height-sm) px-(--space-3) text-[length:var(--text-small)]',
        lg: 'px-(--space-5) py-(--space-3) text-[length:var(--text-lead)]',
        icon: 'size-8 p-0',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Component = asChild ? Slot : 'button';
  return (
    <Component
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
